import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const NAV_LINKS = [
  { to: "/", label: "首页", end: true },
  { to: "/category/movie", label: "电影", end: false },
  { to: "/category/tv", label: "电视剧", end: false },
  { to: "/category/news", label: "资讯", end: false },
  { to: "/about", label: "关于", end: false },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = keyword.trim();
    if (!q) {
      navigate("/search");
      return;
    }
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setKeyword("");
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-all duration-300 ${
        scrolled
          ? "border-background-200 bg-background-50/95 backdrop-blur-md"
          : "border-transparent bg-background-50"
      }`}
    >
      <div className="mx-auto flex h-16 w-full max-w-[1280px] items-center gap-4 px-4 md:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-gradient-to-br from-primary-500 to-primary-700 text-background-50">
            <i className="ri-plant-line text-lg"></i>
          </span>
          <span className="font-heading-role text-lg font-bold tracking-tight text-foreground-950">
            蘑菇<span className="text-primary-600">MOGU2</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.end}
              className={({ isActive }) =>
                `cursor-pointer whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary-100 text-primary-700"
                    : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <form onSubmit={submitSearch} className="ml-auto hidden items-center md:flex">
          <div className="flex h-10 w-[240px] items-center rounded-full border border-background-200 bg-background-100 px-3 transition-colors focus-within:border-primary-300 focus-within:bg-background-50 lg:w-[300px]">
            <span className="flex h-4 w-4 items-center justify-center text-foreground-500">
              <i className="ri-search-line text-base"></i>
            </span>
            <input
              type="text"
              name="q"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影视、演员、关键词"
              className="h-full w-full bg-transparent px-2 text-sm text-foreground-900 outline-none placeholder:text-foreground-500"
            />
            <button
              type="submit"
              className="cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </div>
        </form>

        <button
          type="button"
          aria-label="打开菜单"
          onClick={() => setOpen((prev) => !prev)}
          className="ml-auto flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-800 md:hidden"
        >
          <i className={open ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
        </button>
      </div>

      {open && (
        <div className="border-t border-background-200 bg-background-50 md:hidden">
          <div className="flex w-full flex-col gap-2 px-4 py-4">
            <form onSubmit={submitSearch} className="flex items-center gap-2">
              <div className="flex h-10 flex-1 items-center rounded-full border border-background-200 bg-background-100 px-3">
                <span className="flex h-4 w-4 items-center justify-center text-foreground-500">
                  <i className="ri-search-line text-base"></i>
                </span>
                <input
                  type="text"
                  name="q"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="搜索影视、演员、关键词"
                  className="h-full w-full bg-transparent px-2 text-sm text-foreground-900 outline-none placeholder:text-foreground-500"
                />
              </div>
              <button
                type="submit"
                className="cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-medium text-background-50"
              >
                搜索
              </button>
            </form>
            <nav className="mt-1 flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.end}
                  className={({ isActive }) =>
                    `cursor-pointer rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-primary-100 text-primary-700"
                        : "text-foreground-700 hover:bg-background-100"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}